/**
 * Frustration & Dead Click Detection Engine
 *
 * Detects:
 * - Dead clicks on disabled buttons or unclickable UI elements
 * - Repeated clicks / rapid tapping on non-interactive components
 * - Inactivity stuckness on critical funnel steps
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { captureDeadClick, captureInactivityStuck } from './posthog';

export function useGlobalFrustrationDetector(): void {
  const location = useLocation();
  const screenPath = location.pathname;
  const recentClicksRef = useRef<{ time: number; x: number; y: number; target: EventTarget | null }[]>([]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      const now = Date.now();
      const x = e.clientX;
      const y = e.clientY;

      // Clean up clicks older than 1.5 seconds
      recentClicksRef.current = recentClicksRef.current.filter((c) => now - c.time < 1500);
      recentClicksRef.current.push({ time: now, x, y, target });

      // Check for rapid repeated clicks (3+ clicks within 1.5s in same 30px radius)
      const nearbyClicks = recentClicksRef.current.filter(
        (c) => Math.hypot(c.x - x, c.y - y) < 30,
      );

      if (nearbyClicks.length >= 3) {
        // High click frequency detected
        const button = target.closest('button, [role="button"], a');
        const isDisabled =
          button?.hasAttribute('disabled') ||
          button?.getAttribute('aria-disabled') === 'true' ||
          target.hasAttribute('disabled');

        if (isDisabled) {
          captureDeadClick({
            element_tag: target.tagName.toLowerCase(),
            element_text: target.textContent?.slice(0, 50).trim(),
            element_class: target.className?.toString() || '',
            element_id: target.id || '',
            reason: 'disabled_button',
            screen_path: screenPath,
            x,
            y,
          });
        }
      }

      // Check for dead clicks on disabled buttons directly
      const disabledButton = target.closest('button[disabled], [role="button"][aria-disabled="true"]');
      if (disabledButton) {
        captureDeadClick({
          element_tag: disabledButton.tagName.toLowerCase(),
          element_text: disabledButton.textContent?.slice(0, 50).trim(),
          element_class: disabledButton.className?.toString() || '',
          element_id: disabledButton.id || '',
          reason: 'disabled_button',
          screen_path: screenPath,
          x,
          y,
        });
      }
    };

    window.addEventListener('click', handleClick, true);
    return () => window.removeEventListener('click', handleClick, true);
  }, [screenPath]);
}

/**
 * Hook to detect if a user gets stuck on a screen with no interaction for a given duration.
 */
export function useInactivityTimer({
  screenName,
  thresholdMs = 45000,
  currentStep,
  expectedAction,
  enabled = true,
}: {
  screenName: string;
  thresholdMs?: number;
  currentStep?: number | string;
  expectedAction?: string;
  enabled?: boolean;
}): void {
  const location = useLocation();
  const screenPath = location.pathname;
  const timerRef = useRef<number | undefined>(undefined);
  const firedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!enabled) return;

    firedRef.current = false;
    const resetTimer = () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      if (firedRef.current) return;

      timerRef.current = window.setTimeout(() => {
        firedRef.current = true;
        captureInactivityStuck({
          screen_name: screenName,
          screen_path: screenPath,
          time_idle_ms: thresholdMs,
          current_step: currentStep,
          expected_action: expectedAction,
        });
      }, thresholdMs);
    };

    resetTimer();

    const onActivity = () => {
      if (!firedRef.current) {
        resetTimer();
      }
    };

    window.addEventListener('mousemove', onActivity);
    window.addEventListener('keydown', onActivity);
    window.addEventListener('scroll', onActivity);

    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      window.removeEventListener('mousemove', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('scroll', onActivity);
    };
  }, [screenName, screenPath, thresholdMs, currentStep, expectedAction, enabled]);
}
